import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import {
    Alert,
    Box,
    CircularProgress,
    InputAdornment,
    Paper,
    Popper,
    TextField,
    Typography,
} from '@mui/material';
import { LocalizationKeys } from 'assets/localizations/localizationKeys';
import React from 'react';
import { PaymentTerminalBillingDetails } from 'services/centralServerService';
import { useStores } from 'stores/storeInitializer';
import { NamedAddressSuggestion, searchPhotonAddresses } from './PhotonAddressSearch';
import {
    billingLocalityName,
    isRomanianCounty,
    isRomanianLocality,
    preselectRomanianAddress,
    ROMANIAN_BILLING_COUNTRY_CODE,
    ROMANIAN_COUNTIES,
    ROMANIAN_LOCALITIES_BY_COUNTY,
} from './romanianLocalities';

export type NamedIndividualState = {
    complete: boolean;
    details: PaymentTerminalBillingDetails | null;
};

const emptyAddress = {
    countryCode: '',
    region: '',
    city: '',
    street: '',
    postalCode: '',
    rawAddress: '',
    ready: false,
};

const suggestionLines = (suggestion: NamedAddressSuggestion): { primary: string; secondary: string } => {
    const parts = suggestion.label.split(',').map((part) => part.trim()).filter(Boolean);
    const primary = suggestion.line1.trim() || parts[0] || suggestion.label;
    const secondary = parts
        .slice(1)
        .filter((part, index, list) => part.toLowerCase() !== list[index - 1]?.toLowerCase())
        .join(', ');
    return { primary, secondary };
};

const highlightMatch = (text: string, query: string): React.ReactNode => {
    const words = Array.from(new Set(
        query.trim().split(/\s+/).map((word) => word.trim()).filter((word) => word.length >= 2),
    ));
    if (!words.length) {
        return text;
    }
    const pattern = new RegExp(words.map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|'), 'gi');
    const nodes: React.ReactNode[] = [];
    let lastIndex = 0;
    let match = pattern.exec(text);
    while (match) {
        const index = match.index;
        if (index > lastIndex) {
            nodes.push(text.slice(lastIndex, index));
        }
        nodes.push(
            <Box component="span" key={`${index}-${match[0]}`} sx={{ fontWeight: 700 }}>
                {match[0]}
            </Box>,
        );
        lastIndex = index + match[0].length;
        match = pattern.exec(text);
    }
    if (!nodes.length) {
        return text;
    }
    if (lastIndex < text.length) {
        nodes.push(text.slice(lastIndex));
    }
    return <>{nodes}</>;
};

const addressComplete = (name: string, address: typeof emptyAddress): boolean => Boolean(
    address.ready
    && name.trim()
    && address.countryCode === ROMANIAN_BILLING_COUNTRY_CODE
    && isRomanianCounty(address.region)
    && isRomanianLocality(address.region, address.city)
    && address.street.trim()
);

const billingDetails = (name: string, address: typeof emptyAddress): PaymentTerminalBillingDetails | null => {
    if (!addressComplete(name, address)) {
        return null;
    }
    return {
        schemaVersion: 1,
        recipient: { type: 'INDIVIDUAL', name: name.trim() },
        address: {
            countryCode: address.countryCode,
            region: address.region.trim(),
            city: billingLocalityName(address.city),
            line1: address.street.trim(),
            ...(address.postalCode.trim() ? { postalCode: address.postalCode.trim() } : {}),
            ...(address.rawAddress.trim() ? { rawAddress: address.rawAddress.trim() } : {}),
        },
    };
};

/**
 * Romanian Personal journey: name, Photon search, and the closed country, county, and city lists.
 * Rendered on the same billing page as the Company journey.
 */
export const NamedIndividualForm: React.FC<{
    onChange: (state: NamedIndividualState) => void;
}> = ({ onChange }) => {
    const { store } = useStores();
    const [name, setName] = React.useState('');
    const [query, setQuery] = React.useState('');
    const [address, setAddress] = React.useState(emptyAddress);
    const [suggestions, setSuggestions] = React.useState<NamedAddressSuggestion[]>([]);
    const [searchState, setSearchState] = React.useState<'idle' | 'searching' | 'results' | 'empty' | 'failed'>('idle');
    const [menuDismissed, setMenuDismissed] = React.useState(false);
    const [highlighted, setHighlighted] = React.useState(-1);
    const generation = React.useRef(0);
    const selectedQuery = React.useRef<string | null>(null);
    const fieldRef = React.useRef<HTMLDivElement | null>(null);
    const menuRef = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
        const details = billingDetails(name, address);
        onChange({
            complete: Boolean(details) && searchState !== 'searching',
            details,
        });
    }, [name, address, searchState, onChange]);

    React.useEffect(() => {
        const trimmed = query.trim();
        if (trimmed && trimmed === selectedQuery.current) {
            return undefined;
        }
        if (trimmed.length < 3) {
            setSuggestions([]);
            setSearchState('idle');
            return undefined;
        }
        const currentGeneration = generation.current;
        const controller = new AbortController();
        const timer = window.setTimeout(() => {
            setSearchState('searching');
            const addressLanguage = store.language === 'ro' ? 'ro' : 'en';
            searchPhotonAddresses(trimmed, controller.signal, ROMANIAN_BILLING_COUNTRY_CODE, addressLanguage)
                .then((next) => {
                    if (generation.current !== currentGeneration) {
                        return;
                    }
                    setSuggestions(next);
                    setHighlighted(next.length ? 0 : -1);
                    setSearchState(next.length ? 'results' : 'empty');
                    if (!next.length) {
                        setAddress((current) => (generation.current === currentGeneration
                            ? { ...current, ready: true }
                            : current));
                    }
                })
                .catch(() => {
                    if (controller.signal.aborted || generation.current !== currentGeneration) {
                        return;
                    }
                    setSuggestions([]);
                    setSearchState('failed');
                    setAddress((current) => (generation.current === currentGeneration
                        ? { ...current, ready: true }
                        : current));
                });
        }, 400);
        return () => {
            window.clearTimeout(timer);
            controller.abort();
        };
    }, [query, store.language]);

    const menuVisible = !menuDismissed && (
        suggestions.length > 0 || searchState === 'empty' || searchState === 'failed'
    );

    React.useEffect(() => {
        if (!menuVisible) {
            return undefined;
        }
        const onPointerDown = (event: MouseEvent) => {
            const target = event.target as Node;
            if (fieldRef.current?.contains(target) || menuRef.current?.contains(target)) {
                return;
            }
            setMenuDismissed(true);
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [menuVisible]);

    const clearSelection = () => {
        generation.current += 1;
        selectedQuery.current = null;
        setSuggestions([]);
        setSearchState('idle');
        setMenuDismissed(false);
        setHighlighted(-1);
        setAddress(emptyAddress);
    };

    const selectAddress = (suggestion: NamedAddressSuggestion) => {
        generation.current += 1;
        selectedQuery.current = suggestion.label.trim();
        setSuggestions([]);
        setSearchState('idle');
        setHighlighted(-1);
        const selected = preselectRomanianAddress(suggestion);
        setQuery(suggestion.label);
        setAddress({
            countryCode: selected.countryCode,
            region: selected.region,
            city: selected.city,
            street: suggestion.line1.trim() || suggestion.label.split(',')[0].trim(),
            postalCode: suggestion.postalCode || '',
            rawAddress: suggestion.rawAddress,
            ready: true,
        });
    };

    const onQueryChange = (value: string) => {
        clearSelection();
        setQuery(value);
    };

    const onKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Escape') {
            setMenuDismissed(true);
            return;
        }
        if (!menuVisible || !suggestions.length) {
            return;
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlighted((current) => (current + 1) % suggestions.length);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlighted((current) => (current <= 0 ? suggestions.length - 1 : current - 1));
        } else if (event.key === 'Enter' && highlighted >= 0) {
            event.preventDefault();
            selectAddress(suggestions[highlighted]);
        }
    };

    return (
        <>
            <TextField
                label={store.L(LocalizationKeys.BillingDetailsName)}
                value={name}
                onChange={(event) => setName(event.target.value)}
                fullWidth
            />
            <Box ref={fieldRef}>
                <TextField
                    label={store.L(LocalizationKeys.BillingDetailsAddressSearch)}
                    value={query}
                    onChange={(event) => onQueryChange(event.target.value)}
                    onFocus={() => setMenuDismissed(false)}
                    onKeyDown={onKeyDown}
                    fullWidth
                    autoComplete="off"
                    inputProps={{
                        role: 'combobox',
                        'aria-expanded': menuVisible,
                        'aria-autocomplete': 'list',
                        'aria-controls': menuVisible ? 'billing-address-suggestions' : undefined,
                    }}
                    InputProps={{
                        endAdornment: searchState === 'searching' ? (
                            <InputAdornment position="end">
                                <CircularProgress size={18} />
                            </InputAdornment>
                        ) : undefined,
                    }}
                />
                <Popper
                    open={menuVisible}
                    anchorEl={fieldRef.current}
                    placement="bottom-start"
                    modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
                    style={{ zIndex: 1400, width: fieldRef.current?.clientWidth }}
                >
                    <Paper
                        ref={menuRef}
                        id="billing-address-suggestions"
                        role="listbox"
                        elevation={0}
                        sx={{
                            borderRadius: '8px',
                            backgroundColor: '#fff',
                            boxShadow: '0 2px 6px rgba(60, 64, 67, 0.3), 0 1px 2px rgba(60, 64, 67, 0.15)',
                            overflow: 'auto',
                            maxHeight: 320,
                            py: 0.5,
                        }}
                    >
                        {suggestions.map((suggestion, index) => {
                            const lines = suggestionLines(suggestion);
                            return (
                                <Box
                                    component="button"
                                    type="button"
                                    key={`${suggestion.label}-${index}`}
                                    aria-label={suggestion.label}
                                    onMouseDown={(event) => event.preventDefault()}
                                    onMouseEnter={() => setHighlighted(index)}
                                    onClick={() => selectAddress(suggestion)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1.5,
                                        width: '100%',
                                        px: 1.5,
                                        py: 1,
                                        border: 0,
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        font: 'inherit',
                                        color: 'inherit',
                                        backgroundColor: highlighted === index ? '#f1f3f4' : 'transparent',
                                    }}
                                >
                                    <LocationOnOutlinedIcon sx={{ color: '#70757a', fontSize: 22, mt: '1px' }} />
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography component="span" sx={{ display: 'block', fontSize: 14, lineHeight: 1.3, color: '#202124' }}>
                                            {highlightMatch(lines.primary, query)}
                                        </Typography>
                                        {lines.secondary ? (
                                            <Typography component="span" sx={{ display: 'block', fontSize: 12, lineHeight: 1.35, color: '#70757a' }}>
                                                {highlightMatch(lines.secondary, query)}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                </Box>
                            );
                        })}
                        {!suggestions.length && (
                            <Typography sx={{ px: 2, py: 1.25, fontSize: 13, color: '#70757a' }}>
                                {store.L(searchState === 'failed'
                                    ? LocalizationKeys.BillingDetailsAddressSearchFailed
                                    : LocalizationKeys.BillingDetailsAddressNoResults)}
                            </Typography>
                        )}
                    </Paper>
                </Popper>
            </Box>
            {address.ready && (
                <>
                    <TextField
                        select
                        SelectProps={{ native: true }}
                        label={store.L(LocalizationKeys.BillingDetailsCountry)}
                        value={address.countryCode}
                        onChange={(event) => {
                            const countryCode = event.target.value === ROMANIAN_BILLING_COUNTRY_CODE
                                ? ROMANIAN_BILLING_COUNTRY_CODE
                                : '';
                            setAddress((current) => ({
                                ...current,
                                countryCode,
                                ...(countryCode ? {} : { region: '', city: '' }),
                            }));
                        }}
                        fullWidth
                    >
                        <option value="">{store.L(LocalizationKeys.BillingDetailsSelectOption)}</option>
                        <option value={ROMANIAN_BILLING_COUNTRY_CODE}>
                            {store.L(LocalizationKeys.BillingDetailsCountryRomania)}
                        </option>
                    </TextField>
                    <TextField
                        select
                        SelectProps={{ native: true }}
                        label={store.L(LocalizationKeys.BillingDetailsCounty)}
                        value={address.region}
                        onChange={(event) => {
                            const region = isRomanianCounty(event.target.value) ? event.target.value : '';
                            setAddress((current) => ({ ...current, region, city: '' }));
                        }}
                        fullWidth
                        disabled={address.countryCode !== ROMANIAN_BILLING_COUNTRY_CODE}
                    >
                        <option value="">{store.L(LocalizationKeys.BillingDetailsSelectOption)}</option>
                        {ROMANIAN_COUNTIES.map((county) => (
                            <option key={county} value={county}>{county}</option>
                        ))}
                    </TextField>
                    <TextField
                        select
                        SelectProps={{ native: true }}
                        label={store.L(LocalizationKeys.BillingDetailsCity)}
                        value={address.city}
                        onChange={(event) => {
                            const city = event.target.value;
                            setAddress((current) => ({
                                ...current,
                                city: isRomanianLocality(current.region, city) ? city : '',
                            }));
                        }}
                        fullWidth
                        disabled={!isRomanianCounty(address.region)}
                    >
                        <option value="">{store.L(LocalizationKeys.BillingDetailsSelectOption)}</option>
                        {(ROMANIAN_LOCALITIES_BY_COUNTY[address.region] || []).map((city) => (
                            <option key={city} value={city}>{city}</option>
                        ))}
                    </TextField>
                    <TextField
                        label={store.L(LocalizationKeys.BillingDetailsStreet)}
                        value={address.street}
                        onChange={(event) => setAddress((current) => ({ ...current, street: event.target.value }))}
                        fullWidth
                    />
                </>
            )}
            {(query || address.ready) && searchState !== 'searching' && !addressComplete(name, address) && (
                <Alert severity="info">
                    {store.L(LocalizationKeys.BillingDetailsNamedIncomplete)}
                </Alert>
            )}
        </>
    );
};
